/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';

// Lib
import { apiClient } from '@lib/api-client';
import {
	queryClient,
	TMutationConfig,
} from '@lib/react-query';

// I18n
import { useTranslation } from 'react-i18next';

// Types
import { TApiError, TUser } from '@global-types/api/api';
import { TApiResponseUpdateUser } from '../types';

// Constants
import { queryKeys } from '@config/constants/query-keys';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */

// Update user
export const updateUser = (args: {
	id: string;
	body: Partial<TUser>;
}): Promise<TApiResponseUpdateUser> => {
	const { id, body } = args;
	return apiClient.patch(`/users/${id}`, body);
};

/* -------------------------------------------------------------------------- */
/*                                   Hooks                                    */
/* -------------------------------------------------------------------------- */
export const useUpdateUser = (args: {
	page: number;
	size: number;
	mutationConfig?: TMutationConfig<typeof updateUser>;
}) => {
	const { page, size, mutationConfig } = args;
	const { t } = useTranslation();

	const { onSuccess } = mutationConfig || {};

	return useMutation({
		mutationFn: async (args: {
			id: string;
			body: any;
		}) => {
			return updateUser(args);
		},

		onSuccess: async (data, ...args) => {
			notifications.show({
				title: t('global.success'),
				message: t(
					'users.notifications.userSuccessfullyUpdated'
				),
				color: 'var(--mantine-color-igluGreen-9)',
			});

			queryClient.invalidateQueries({
				queryKey: [
					queryKeys.app.users.list.key,
					{ page: page, size: size },
				],
			});

			onSuccess?.(data, ...args);
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'var(--mantine-color-igluRed-9)',
			});
		},
	});
};
